import type { MimirClipDetails, Clip } from './clip';
import type { ProjectItemInfo } from './projectItem';

/*
  This constant is described in Adobe Premiere CEF examples such as:
  https://github.com/Adobe-CEP/Samples/blob/f86975c3689e29df03e7d815c3bb874045b7f991/PProPanel/jsx/PPRO/Premiere.jsx#L743
  https://ppro-scripting.docsforadobe.dev/other/time/#description
*/
export const premiereTicksPerSecond = 254016e6;
export interface SequenceDetails {
  projectType?: 'prproj' | 'fcpx';
  tracks?: Track[];
  sequenceInPointInMs: number;
  sequenceOutPointInMs: number;
}

export interface SequenceExportInfo {
  exportedFilename: string;
  exportedFileExtension: string;
  parentItemIds: string[];
  tracks: Track[];
  /**
   * A map of Mimir items that are specifically part of the portion of a sequence being exported.
   * Keys are Mimir IDs. They contain objects that are keyed by a combination of the clip's
   * file path and a 'node ID' assigned by premiere and values are title of the clip in the bin.
   */
  mimirClipInfos: Record<string, Record<string, string>>;
  errorMessage?: string;
  warnings?: string[];
  sequenceKey: string;    // with project path, more unique
  sequenceName: string;
  sequenceId: string;     // without project path, not reliably unique in multi project env
  sequenceInPointInMs: number;
  sequenceOutPointInMs: number;
  projectPath: string;
}

export interface SequenceInfo {
  exportInfo: SequenceExportInfo;
  itemInfos: ProjectItemInfo[];
}

export enum MediaType {
  VIDEO = 'video',
  AUDIO = 'audio',
}

export interface Track {
  id?: number;              // number, given by premiere. Example: 1, 2
  name?: string;            // user-editable. Example: 'Mono', 'Speak'
  mediaType?: MediaType;    // 'audio' or 'video'
  clips: Clip[];
  trackLabel: string;       // synthesized from track type and array order. Example: 'V1', 'A2'
}

export type TimelineInsertion = {
  // Offset in milliseconds of the position at which to insert the clip, on the sequence/timeline.
  start: number;

  // Whether to insert video or audio from the clip into the timeline. If not set, both video and audio will be inserted.
  mediaType: MediaType | undefined;

  // To which track the clip should be inserted. E.g., if mediaType is audio and trackIndex is 1,
  // the clip will be inserted into the second audio track. If not set, the clip will be inserted
  // into the first track.
  trackIndex: number;

  // This is used to indicate which channel of the source clip should be mapped to the single channel of
  // the audio subclip being added to trackIndex. E.g., if the 5th channel of source clip should be taken,
  // audioMapping will be [[0.0, 0.0, 0.0, 0.0, 1.0]].
  audioMapping: number[][];
};

export interface TimelineSummary {
  id: string;
  name: string;
}

export interface SequencePositionDetails {
  mimirClipDetails?: MimirClipDetails;
}